// src/services/links.js — link previews.
//
// 🟥 linkMetadata IS PLAINTEXT, and deliberately so on both clients. It sits on the
//    message doc beside the sealed `text` and the redacted `replyTo`, unencrypted,
//    because it describes a PUBLIC page — and because the app reads
//    `message.linkMetadata` directly with no unseal step, so a sealed blob would render
//    as nothing at all over there. Match the app; don't improve on it here.
//
// The shape is fixed at exactly eight keys (ChatModal ~18448), and `images`/`favicons`
// are ARRAYS. The phone's card does `images.slice(0, 3)` — hand it a bare string and it
// renders an empty card with no error.

import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { BACKEND_URL, getSocket } from './socket';

// 🟥 THE SAME REGEX THE PHONE USES, byte for byte, first match only, scheme required.
//    A cleverer detector (bare domains, trailing-punctuation trimming) would find URLs
//    the phone doesn't, and the backfill key on both sides is the raw matched string —
//    so the moment the two disagree about what "the URL" is, a preview sent from here
//    can never be matched to its message over there.
const URL_RE = /(https?:\/\/[^\s]+)/;

export const firstUrl = (text) => (String(text || '').match(URL_RE) || [])[0] || null;

/** Coerce anything that arrives — object, JSON string, junk — into the 8-key shape. */
export function normalizeLink(raw) {
  let m = raw;
  if (typeof m === 'string') { try { m = JSON.parse(m); } catch { return null; } }
  if (!m || typeof m !== 'object') return null;
  if (!m.title && !m.url) return null;
  const arr = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x) : []);
  return {
    title: m.title || null,
    description: m.description || null,
    images: arr(m.images),
    favicons: arr(m.favicons),
    url: m.url || null,
    siteName: m.siteName || null,
    mediaType: m.mediaType || null,
    contentType: m.contentType || null,
  };
}

// ── lookup ─────────────────────────────────────────────────────────────────
//
// 🟢 The browser can't scrape Open Graph tags itself: a cross-origin fetch to an
//    arbitrary site is blocked by CORS, and `user-agent` — which is what makes most
//    sites serve OG tags at all — is a forbidden header. So this asks the signaling
//    server, which grew a /api/link-preview endpoint alongside /api/flight-info.
const CACHE_KEY = 'allo_link_cache';
const TTL = 6 * 60 * 60 * 1000;
const MAX_CACHE = 80;
const mem = new Map();       // url -> { at, data|null }
const inflight = new Map();

(function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    Object.entries(raw).forEach(([k, v]) => { if (v?.at) mem.set(k, v); });
  } catch {}
}());
function persist() {
  try {
    const top = [...mem.entries()].sort((a, b) => b[1].at - a[1].at).slice(0, MAX_CACHE);
    localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(top)));
  } catch {}
}

/** Cached answer without touching the network: data, null (known miss), or undefined. */
export function cachedLink(url) {
  const hit = mem.get(url);
  if (!hit) return undefined;
  if (Date.now() - hit.at > TTL) { mem.delete(url); return undefined; }
  return hit.data;
}

/** Never throws. Returns the metadata or null. */
export function fetchLink(url) {
  if (!url) return Promise.resolve(null);
  const hit = cachedLink(url);
  if (hit !== undefined) return Promise.resolve(hit);
  if (inflight.has(url)) return inflight.get(url);

  const p = (async () => {
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 9000) : null;
    try {
      const res = await fetch(`${BACKEND_URL}/api/link-preview?url=${encodeURIComponent(url)}`, {
        signal: ctrl ? ctrl.signal : undefined,
      });
      if (!res.ok) { mem.set(url, { at: Date.now(), data: null }); persist(); return null; }
      const data = normalizeLink(await res.json());
      mem.set(url, { at: Date.now(), data }); persist();
      return data;
    } catch {
      // a network blip is NOT a "this page has no preview" — don't poison the cache
      return null;
    } finally {
      if (timer) clearTimeout(timer);
      inflight.delete(url);
    }
  })();
  inflight.set(url, p);
  return p;
}

// ── backfill ───────────────────────────────────────────────────────────────

/**
 * A message went out before its preview resolved. Patch it everywhere it exists.
 *
 * 🟥 Three destinations, and the socket one is not optional. In a 1:1 chat the server's
 *    store-and-forward sweep deletes the message doc once it's delivered, so `updateDoc`
 *    very often lands on nothing — that's expected, not an error. The
 *    `link-preview-update` event is what actually reaches the recipient, and it is keyed
 *    on `clientTempId` rather than the doc id, because the doc id is exactly the thing
 *    that may no longer exist.
 */
export async function backfillLink({ chatId, messageId, clientTempId, participants, myUid, metadata }) {
  const meta = normalizeLink(metadata);
  if (!meta || !chatId) return;
  try {
    await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), { linkMetadata: meta });
  } catch { /* swept by store-and-forward — the socket below is the real delivery */ }

  const s = getSocket();
  if (!s || !clientTempId) return;
  (participants || []).filter((u) => u && u !== myUid).forEach((toUserId) => {
    try {
      // an OBJECT here — the phone JSON.stringifies only on `new-message`, and its
      // link-preview-update handler reads the object directly
      s.emit('link-preview-update', { chatId, toUserId, clientTempId, linkMetadata: meta, fromUserId: myUid });
    } catch {}
  });
}

/** Subscribe to previews arriving for messages already on screen. */
export function watchLinkUpdates(cb) {
  const s = getSocket();
  if (!s) return () => {};
  const h = (d) => {
    const meta = normalizeLink(d?.linkMetadata);
    if (d?.chatId && d?.clientTempId && meta) cb(d.chatId, d.clientTempId, meta);
  };
  s.on('link-preview-update', h);
  return () => { try { s.off('link-preview-update', h); } catch {} };
}

/** Maps links open natively on the phone; on the web a new tab is the honest equivalent. */
export function openLink(url) {
  if (!url) return;
  try { window.open(url, '_blank', 'noopener,noreferrer'); } catch {}
}
